<template>
  <div class="operate-page" v-loading="loading">
    <el-form size="small" inline>
      <el-form-item label="统计时间">
        <el-date-picker v-model="dateRange" type="daterange" value-format="yyyy-MM-dd" unlink-panels
          range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" :clearable="false" />
      </el-form-item>
      <el-form-item label="汇总">
        <el-radio-group v-model="grain">
          <el-radio-button label="week">按周</el-radio-button>
          <el-radio-button label="month">按月</el-radio-button>
          <el-radio-button label="year">按年</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="query">查询</el-button>
      </el-form-item>
      <el-form-item>
        <ai-insight ref="insight" />
      </el-form-item>
    </el-form>

    <p class="tip">固定电车车队。上座率按实际人数加权。毛收入 = 合计流水 − 电费 − 过路费 − 车租 − 工资。金额超过 1 万时用「万」显示。按周查看时，日期不要超过约 4 个月。</p>

    <el-row :gutter="12" class="cards">
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatAmount(summary.baseIncome) }}</div>
          <div class="label">流水</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatAmount(summary.totalIncome) }}</div>
          <div class="label">合计流水</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatRate(summary.actAttendance) }}</div>
          <div class="label">实际上座率</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatAmount(summary.variableCost) }}</div>
          <div class="label">变动成本</div>
          <div class="sub">电费 + 过路费</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num">{{ formatAmount(summary.fixedCost) }}</div>
          <div class="label">固定成本</div>
          <div class="sub">车租 + 工资</div>
        </el-card>
      </el-col>
      <el-col :span="4">
        <el-card shadow="never">
          <div class="num" :class="{ minus: Number(summary.pureIncome) < 0 }">{{ formatAmount(summary.pureIncome) }}</div>
          <div class="label">毛收入</div>
          <div class="sub">单边 {{ formatAmount(summary.purePerRoute) }}</div>
        </el-card>
      </el-col>
    </el-row>
    <p class="tip">单人变动成本 {{ formatAmount(summary.variablePerPassenger) }}，按实际人数分摊电费和过路费。</p>

    <div class="block-title">流水、毛收入和上座率</div>
    <chart ref="trendChart" :height="340" />

    <div class="block-title">成本构成</div>
    <chart ref="costChart" :height="280" />

    <div class="block-title">司机明细</div>
    <el-table :data="drivers" stripe max-height="520" show-summary :summary-method="getSummaries" empty-text="该时段没有司机结算">
      <el-table-column prop="driverName" label="姓名" width="100" fixed />
      <el-table-column prop="driverNo" label="编号" width="90" />
      <el-table-column prop="routeNum" label="边数" width="90" sortable />
      <el-table-column prop="actAttendance" label="实际上座率" width="120" sortable>
        <template slot-scope="scope">{{ scope.row.actAttendance }}%</template>
      </el-table-column>
      <el-table-column prop="baseIncome" label="流水" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.baseIncome) }}</template>
      </el-table-column>
      <el-table-column prop="powerFee" label="电费" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.powerFee) }}</template>
      </el-table-column>
      <el-table-column prop="tollFee" label="过路费" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.tollFee) }}</template>
      </el-table-column>
      <el-table-column prop="carRentCost" label="车租" min-width="100" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.carRentCost) }}</template>
      </el-table-column>
      <el-table-column prop="prueCommissionAmount" label="毛收入" min-width="110" sortable>
        <template slot-scope="scope">{{ formatAmount(scope.row.prueCommissionAmount) }}</template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { getTeamOperateAnalysis } from "@/api/data";
import { DateUtil } from "@/util";
import AiInsight from "@/components/AiInsight";

function defaultDateRange() {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth() - 5, 1);
  return [DateUtil.format(start, "yyyy-MM-dd"), DateUtil.format(end, "yyyy-MM-dd")];
}

function yuan(value) {
  return Math.round(Number(value || 0)) / 100;
}

function formatYuan(value) {
  const number = Number(value || 0);
  const sign = number < 0 ? "-" : "";
  const abs = Math.abs(number);
  if (abs >= 10000) {
    return `${sign}${(abs / 10000).toFixed(2)}万`;
  }
  return sign + abs.toLocaleString("zh-CN", { maximumFractionDigits: 0 });
}

function costOf(row) {
  return Number(row.variableCost || 0) + Number(row.fixedCost || 0);
}

function highlightDrivers(drivers) {
  const list = drivers || [];
  const lowMargin = list.slice().sort((a, b) => Number(a.prueCommissionAmount || 0) - Number(b.prueCommissionAmount || 0)).slice(0, 3);
  const highCost = list.slice().sort((a, b) => costOf(b) - costOf(a)).slice(0, 3);
  const seen = {};
  const rows = [];
  lowMargin.concat(highCost).forEach((row) => {
    const key = row.driverNo || row.driverName;
    if (seen[key]) {
      return;
    }
    seen[key] = true;
    rows.push({
      driverName: row.driverName,
      driverNo: row.driverNo,
      routeNum: row.routeNum,
      actAttendance: row.actAttendance,
      baseIncome: row.baseIncome,
      powerFee: row.powerFee,
      tollFee: row.tollFee,
      carRentCost: row.carRentCost,
      baseSalaryCost: row.baseSalaryCost,
      variableCost: row.variableCost,
      fixedCost: row.fixedCost,
      prueCommissionAmount: row.prueCommissionAmount
    });
  });
  return rows;
}

export default {
  components: { AiInsight },
  data() {
    return {
      dateRange: defaultDateRange(),
      grain: "month",
      loading: false,
      summary: {},
      drivers: []
    };
  },
  created() {
    this.query();
  },
  methods: {
    formatRate(value) {
      if (value == null || value === "") {
        return "—";
      }
      return `${Number(value).toFixed(1)}%`;
    },
    formatMoney(value, precision) {
      if (value == null || value === "") {
        return "—";
      }
      return (Number(value) / 100).toFixed(precision);
    },
    formatAmount(value) {
      if (value == null || value === "") {
        return "—";
      }
      return formatYuan(yuan(value));
    },
    query() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning("请选择日期");
        return;
      }
      this.loading = true;
      getTeamOperateAnalysis({
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        grain: this.grain
      }).then((res) => {
        const data = res.data || {};
        this.summary = data.summary || {};
        this.drivers = data.drivers || [];
        this.renderTrend(data.trend || []);
        this.renderCost(this.summary);
        this.$nextTick(() => {
          if (!this.$refs.insight) {
            return;
          }
          this.$refs.insight.setContext("operate", {
            grain: this.grain,
            dateRange: this.dateRange,
            summary: this.summary,
            trend: data.trend || [],
            drivers: highlightDrivers(this.drivers)
          });
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    renderTrend(trend) {
      const option = {
        tooltip: {
          trigger: "axis",
          formatter(params) {
            const lines = [params[0].axisValue];
            params.forEach((item) => {
              const text = item.seriesName === "上座率" ? `${item.data}%` : formatYuan(item.data);
              lines.push(`${item.marker}${item.seriesName}：${text}`);
            });
            return lines.join("<br/>");
          }
        },
        legend: { data: ["流水", "毛收入", "上座率"] },
        grid: { left: 72, right: 56, bottom: 32, top: 48 },
        xAxis: { type: "category", data: trend.map((item) => item.period) },
        yAxis: [
          { type: "value", axisLabel: { formatter: (value) => formatYuan(value) } },
          { type: "value", name: "上座率", axisLabel: { formatter: "{value}%" } }
        ],
        series: [
          { name: "流水", type: "line", data: trend.map((item) => yuan(item.baseIncome)) },
          { name: "毛收入", type: "line", data: trend.map((item) => yuan(item.pureIncome)) },
          { name: "上座率", type: "line", yAxisIndex: 1, data: trend.map((item) => item.actAttendance) }
        ]
      };
      this.$nextTick(() => {
        if (this.$refs.trendChart) {
          this.$refs.trendChart.setOption(option, true);
        }
      });
    },
    renderCost(summary) {
      const option = {
        tooltip: {
          trigger: "axis",
          formatter(params) {
            const item = params[0];
            return `${item.name}<br/>${item.marker}${formatYuan(item.data)}`;
          }
        },
        grid: { left: 72, right: 24, bottom: 32, top: 24 },
        xAxis: { type: "category", data: ["电费", "过路费", "车租", "工资"] },
        yAxis: { type: "value", axisLabel: { formatter: (value) => formatYuan(value) } },
        series: [{
          type: "bar",
          barWidth: 36,
          data: [yuan(summary.powerFee), yuan(summary.tollFee), yuan(summary.carRentCost), yuan(summary.baseSalaryCost)]
        }]
      };
      this.$nextTick(() => {
        if (this.$refs.costChart) {
          this.$refs.costChart.setOption(option, true);
        }
      });
    },
    getSummaries({ columns, data }) {
      const moneyPrecision = {
        baseIncome: 0,
        powerFee: 2,
        tollFee: 2,
        carRentCost: 2,
        prueCommissionAmount: 2
      };
      return columns.map((column, index) => {
        if (index === 0) {
          return "合计";
        }
        if (column.property === "routeNum") {
          return data.reduce((sum, row) => sum + Number(row.routeNum || 0), 0);
        }
        if (column.property === "actAttendance") {
          let fullNum = 0;
          let realNum = 0;
          data.forEach((row) => {
            fullNum += Number(row.routeNum || 0) * Number(row.seats || 4);
            realNum += Number(row.num || 0);
          });
          return fullNum ? `${((realNum / fullNum) * 100).toFixed(1)}%` : "—";
        }
        if (Object.prototype.hasOwnProperty.call(moneyPrecision, column.property)) {
          const total = data.reduce((sum, row) => sum + Number(row[column.property] || 0), 0);
          return this.formatAmount(total);
        }
        return "";
      });
    }
  }
};
</script>

<style lang="less" scoped>
.tip {
  color: #909399;
  font-size: 13px;
  line-height: 22px;
  margin: 0 0 12px;
}

.cards {
  margin-bottom: 4px;
}

.num {
  font-size: 22px;
  font-weight: 600;
  line-height: 32px;
}

.minus {
  color: #f56c6c;
}

.label {
  color: #606266;
  font-size: 13px;
}

.sub {
  color: #909399;
  font-size: 12px;
  margin-top: 4px;
}

.block-title {
  margin: 18px 0 8px;
  font-size: 15px;
  font-weight: 600;
}
</style>
